import { useAppConstantsContext } from '@ltvco/refresh-lib/ctx';
import {
  FamilyTreeContent,
  FamilyTreeContentProvider,
  useAncestryTreeV2,
  usePostAncestryTree,
  formatPostIndiFromOwner,
  getCleanAncestryTreeData,
  type JsonGedcomData,
  type PostIndi,
} from '@ltvco/refresh-lib/fmt';
import { Stack } from '@ltvco/refresh-lib/theme';
import {
  enqueueSnackbar,
  LoadingDots,
  useReport,
  ReportFactory,
  serializedTypes,
  useReportMonitors,
} from '@ltvco/refresh-lib/v1';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { constants } from 'appConstants';
import { getClaimedReports } from 'utils/getClaimedReports';

const REPORT_TYPE = 'detailed_person_report';
const ANCESTRY_TREE_INITIAL_MEMBER_COUNT = 6;
const ANCESTRY_TREE_MEMBER_COUNT_FOR_TWO_MEMBERS = 9;

const LoadingContent = () => (
  <Stack
    sx={{
      width: '100vw',
      height: '100vh',
      paddingTop: 10,
    }}
  >
    <LoadingDots count={5} />
  </Stack>
);

export const FamilyTreePage = () => {
  const {
    data: { brandName },
  } = useAppConstantsContext();

  useEffect(() => {
    document.title = `Family Tree - ${brandName}`;
  }, []);

  const urlParams = new URLSearchParams(window.location.search);
  const [openMenuParam, setOpenMenuParam] = useState<string | undefined>();
  const [claimedReportPermalink, setClaimedReportPermalink] = useState<
    string | undefined
  >(undefined);
  const permalinkParam = urlParams.get('from') || undefined;

  const { currentMonitors, reportMonitorIsLoading } = useReportMonitors();

  const openParam = urlParams.get('open') ?? undefined;

  useEffect(() => {
    // If the user has been redirected from the relatives section on the person report
    // it has a query param 'open=quick-add-relatives' to open the quick add relatives menu
    // Once we opened the menu, we remove the query param from the URL Cesar C. 03/05/2024
    if (openParam) {
      setOpenMenuParam(openParam);
      urlParams.delete('open');
      const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
      window.history.replaceState({}, '', newUrl);
    }
  }, [openParam]);

  useEffect(() => {
    if (reportMonitorIsLoading) {
      return;
    }
    if (!currentMonitors.length) {
      return;
    }
    if (claimedReportPermalink) {
      return;
    }

    const claimedReports = getClaimedReports(currentMonitors, REPORT_TYPE);

    if (!claimedReports.length) {
      return;
    }

    setClaimedReportPermalink(claimedReports[0].permalink);
  }, [currentMonitors, reportMonitorIsLoading, claimedReportPermalink]);

  if (reportMonitorIsLoading) {
    return <LoadingContent />;
  }

  return (
    <FamilyTreeWithRelatives
      // If the user has a claimed report, we use it to post to the ancestry tree
      // otherwise we use the permalink from the URL is going to be prioritized
      // but this permalinkParam is going to be removed soon
      permalink={permalinkParam || claimedReportPermalink}
      openMenuParam={openMenuParam}
      cleanMenuParam={() => setOpenMenuParam(undefined)}
    />
  );
};

interface FamilyTreeWithRelativesProps {
  openMenuParam?: string;
  permalink: string | undefined;
  cleanMenuParam: () => void;
}

const FamilyTreeWithRelatives = ({
  permalink,
  openMenuParam,
  cleanMenuParam,
}: FamilyTreeWithRelativesProps) => {
  const [allPossibleRelativesMainReport, setAllPossibleRelativesMainReport] =
    useState<serializedTypes.Connection[]>([]);

  const [selectedRelativesToAdd, setSelectedRelativesToAdd] = useState<
    serializedTypes.Connection[]
  >([]);

  const [isLoading, setIsLoading] = useState(false);
  const [bvid, setBvid] = useState<string>('');
  const [relatives, setRelatives] = useState<
    serializedTypes.Connection[] | undefined
  >(undefined);
  const [reportIndi, setReportIndi] = useState<PostIndi>();
  const [newAncestryTreeData, setNewAncestryTreeData] =
    useState<JsonGedcomData>();
  const [isCleanView, setIsCleanView] = useState<boolean>(false);
  const [
    hasPersonReportRelativeToAddAfterNewTree,
    setHasPersonReportRelativeToAddAfterNewTree,
  ] = useState<boolean>(false);

  const navigate = useNavigate();

  const [
    previousHasPersonReportRelativeToAddAfterNewTree,
    setPreviousHasPersonReportRelativeToAddAfterNewTree,
  ] = useState(false);

  const [localStorageTriggerCount, setLocalStorageTriggerCount] = useState(0);

  const removeRelativeFromLocalStorage = () => {
    localStorage.removeItem('familyTreeRelativeToAdd');
    setLocalStorageTriggerCount((prev) => prev + 1);
  };

  const {
    data: ancestryTreeData,
    isError: getAncestryTreeError,
    error: getAncestryTreeErrorObj,
    isLoading: isAncestryTreeLoading,
  } = useAncestryTreeV2({});

  const {
    links: { baseUrl },
  } = useAppConstantsContext();

  const {
    mutate: postAncestryTreeMutation,
    isError: postAncestryTreeIsError,
    isLoading: isPostingTree,
  } = usePostAncestryTree();

  const { data: coreResultData } = useReport(permalink || '', !permalink);

  const handleNavigateToTreeBuilder = () => {
    window.location.href = `${baseUrl}/dashboard/family-ancestry?open=tree-builder&from=${permalink}`;
  };

  const handleCleanViewToggle = (newValue: boolean) => {
    setIsCleanView(newValue);
  };

  useEffect(() => {
    if (coreResultData) {
      const report = ReportFactory.create(coreResultData, 'person');
      const owner = report.getOwner({
        potentialOwnerIndex: 0,
        showHighConfidenceDataOnly: true,
        higherConfidenceThreshold: constants.config.higherConfidenceThreshold,
      });
      const { relatives, identity } = owner;
      const reportBvid = identity!.ids[0];

      const reportIndi = formatPostIndiFromOwner(owner);
      if (reportIndi) {
        setReportIndi(reportIndi);
      }

      setRelatives(relatives);
      setBvid(reportBvid);
    }
  }, [coreResultData]);

  useEffect(() => {
    if (isAncestryTreeLoading || isPostingTree || ancestryTreeData) {
      return;
    }

    if (getAncestryTreeError && !!reportIndi) {
      // if the request doesn't have a valid token avoid posting to ancestry tree.
      // @ts-expect-error - Checking if the error message includes 401 TODO: Fix this
      if (getAncestryTreeErrorObj?.message.includes('401')) {
        return;
      }

      postAncestryTreeMutation({
        indi: reportIndi,
        permalink: permalink || '',
        bvid,
      });

      setReportIndi(undefined);
    }
  }, [getAncestryTreeError, getAncestryTreeErrorObj, reportIndi]);

  useEffect(() => {
    if (ancestryTreeData) {
      // Get the current state value directly before using it
      const currentCleanViewState = isCleanView;

      // If the user is in clean view, we filter out the deleted individuals and placeholders from the tree
      if (currentCleanViewState) {
        const newAncestryTreeData = getCleanAncestryTreeData(ancestryTreeData);
        setNewAncestryTreeData(newAncestryTreeData);
      } else {
        setNewAncestryTreeData(ancestryTreeData);
      }
    }
  }, [ancestryTreeData, isCleanView]);

  useEffect(() => {
    if (!!ancestryTreeData && isLoading) {
      if (
        ancestryTreeData.indis.length <= ANCESTRY_TREE_INITIAL_MEMBER_COUNT &&
        localStorage.getItem('skipAutoTreeBuilder') !== 'true'
      ) {
        handleNavigateToTreeBuilder();
      } else {
        setIsLoading(false);
      }
    }
  }, [ancestryTreeData, setIsLoading, localStorage]);

  useEffect(() => {
    if (!!ancestryTreeData?.indis && localStorageTriggerCount <= 1) {
      const ancestryTreePopulatedMember = ancestryTreeData.indis.filter(
        (member) => member.deleted_at == null && !member.placeholder
      ).length;
      const hasPersonReportRelativeToAdd = JSON.parse(
        localStorage.getItem('familyTreeRelativeToAdd') || '{}'
      )?.person_report_params;

      if (ancestryTreePopulatedMember === 1 && !!hasPersonReportRelativeToAdd) {
        setHasPersonReportRelativeToAddAfterNewTree(true);
        setPreviousHasPersonReportRelativeToAddAfterNewTree(false);
      } else {
        setHasPersonReportRelativeToAddAfterNewTree(false);
        setPreviousHasPersonReportRelativeToAddAfterNewTree(true);
      }
    }
  }, [
    ancestryTreeData,
    localStorageTriggerCount,
    setPreviousHasPersonReportRelativeToAddAfterNewTree,
    setHasPersonReportRelativeToAddAfterNewTree,
  ]);

  if (postAncestryTreeIsError) {
    enqueueSnackbar('An error occurred while generating the family tree', {
      variant: 'error',
    });

    navigate('/dashboard/family-ancestry');
  }

  const eligibleMaxMemberCount =
    !hasPersonReportRelativeToAddAfterNewTree &&
    !!previousHasPersonReportRelativeToAddAfterNewTree
      ? ANCESTRY_TREE_MEMBER_COUNT_FOR_TWO_MEMBERS
      : ANCESTRY_TREE_INITIAL_MEMBER_COUNT;

  const thereIsPossibleRelatives = relatives && relatives?.length > 0;
  const addRelativesToNewTree =
    (ancestryTreeData?.indis &&
      ancestryTreeData?.indis?.length <= eligibleMaxMemberCount &&
      selectedRelativesToAdd.length === 0) ||
    allPossibleRelativesMainReport.length > 0;

  const showModalAfterClaimingTree =
    thereIsPossibleRelatives &&
    addRelativesToNewTree &&
    !hasPersonReportRelativeToAddAfterNewTree;

  // Only show loading content during initial load or when posting tree
  const shouldShowLoadingContent =
    (isLoading && !ancestryTreeData) ||
    isPostingTree ||
    (isAncestryTreeLoading && !ancestryTreeData);

  if (shouldShowLoadingContent) {
    return <LoadingContent />;
  }

  return (
    <FamilyTreeContentProvider
      members={newAncestryTreeData?.indis || ancestryTreeData?.indis || []}
      mainReportRelatives={relatives || []}
      addRelativesToNewTree={showModalAfterClaimingTree}
      allPossibleRelativesMainReport={allPossibleRelativesMainReport}
      setAllPossibleRelativesMainReport={setAllPossibleRelativesMainReport}
      selectedRelativesToAdd={selectedRelativesToAdd}
      setSelectedRelativesToAdd={setSelectedRelativesToAdd}
      hasPersonReportRelativeToAddAfterNewTree={
        hasPersonReportRelativeToAddAfterNewTree
      }
      removeRelativeFromLocalStorage={removeRelativeFromLocalStorage}
      openMenuParam={openMenuParam}
      originalMemberId={
        ancestryTreeData?.focused_member_id ??
        ancestryTreeData?.original_member_id
      }
    >
      <FamilyTreeContent
        ancestryTreeData={newAncestryTreeData}
        isAncestryTreeLoading={isAncestryTreeLoading}
        permalinkParam={permalink}
        cleanMenuParam={cleanMenuParam}
        isCleanView={isCleanView}
        setCleanView={handleCleanViewToggle}
      />
    </FamilyTreeContentProvider>
  );
};
